// src/pages/Home.tsx
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowUpRight, Sparkles, PackageOpen } from "lucide-react";

import HeroSection from "../components/home/HeroSection";
import NewArrivals, { NewProduct } from "../components/home/NewArrivals";
import DropSection, { Drop } from "../components/home/DropSection";
import BrandStory from "../components/home/BrandStory";

import {
  getNewProducts,
  getBestSellingProducts,
  getDiscountedProducts,
  ProductListDto,
} from "../api/productApi";

/* ======================
   HELPERS
====================== */
const RAW_API_URL = process.env.REACT_APP_API_URL || "";
const API_URL = RAW_API_URL.replace(/\/+$/, "");
const FALLBACK_IMG = "/images/placeholder.jpg";

function resolveImg(url?: string | null) {
  if (!url) return FALLBACK_IMG;

  // normalize windows path: "\uploads\a.webp" -> "/uploads/a.webp"
  const u = String(url).split("\\").join("/").trim();

  // absolute url
  if (/^https?:\/\//i.test(u)) return u;

  // "/uploads/xxx.webp" -> "https://localhost:5001/uploads/xxx.webp"
  if (u.startsWith("/")) return API_URL ? `${API_URL}${u}` : u;

  // "uploads/xxx.webp" -> "https://localhost:5001/uploads/xxx.webp"
  return API_URL ? `${API_URL}/${u}` : u;
}

/**
 * ✅ IMPORTANT:
 * AppRoutes đã đổi sang /product/:id
 * NewArrivals component đang dùng field "slug" để link/identifier
 * => set slug = String(id) để click đi đúng /product/123
 */
function mapToNewArrivalsItem(p: ProductListDto): NewProduct {
  const id = Number(p?.id ?? 0);

  return {
    product_id: id,
    name: String(p?.name ?? ""),
    base_price: Number(p?.salePrice ?? p?.basePrice ?? 0),
    image_url: resolveImg(p?.thumbnail ?? null),

    // ✅ dùng id làm slug để component NewArrivals không phải sửa mà route vẫn đúng
    slug: String(id),
  };
}

function cn(...x: Array<string | false | undefined | null>) {
  return x.filter(Boolean).join(" ");
}

/* ======================
   SKELETONS
====================== */
function SkeletonBlock({ className }: { className?: string }) {
  return (
    <div
      className={cn(
        "animate-pulse rounded-2xl bg-brand-brown/5 border border-brand-brown/10",
        className
      )}
    />
  );
}

function SectionHead(props: {
  kicker: string;
  title: string;
  desc?: string;
  ctaText?: string;
  ctaTo?: string;
  icon?: React.ReactNode;
}) {
  return (
    <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
      <div>
        <p className="text-[10px] uppercase tracking-[0.62em] text-brand-brown/55">
          {props.kicker}
        </p>
        <h2 className="mt-4 text-[22px] sm:text-[26px] uppercase tracking-[0.32em]">
          {props.title}
        </h2>
        {props.desc && (
          <p className="mt-4 max-w-2xl text-sm leading-[2.0] text-brand-brown/60">
            {props.desc}
          </p>
        )}
      </div>

      {props.ctaTo && props.ctaText && (
        <Link
          to={props.ctaTo}
          className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.55em]
                     text-brand-brown/55 hover:text-brand-brown transition"
        >
          {props.icon}
          {props.ctaText}
          <ArrowUpRight className="w-4 h-4" />
        </Link>
      )}
    </div>
  );
}

/* ======================
   PAGE
====================== */
export default function Home() {
  const { t } = useTranslation();

  const [products, setProducts] = useState<NewProduct[]>([]);
  const [currentDrop, setCurrentDrop] = useState<Drop | null>(null);

  const [bestSelling, setBestSelling] = useState<NewProduct[]>([]);
  const [discounted, setDiscounted] = useState<NewProduct[]>([]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // API_URL là const (env lúc build) => khỏi cần useMemo
  const canFetch = useMemo(() => !!API_URL, []);

  useEffect(() => {
    const controller = new AbortController();
    const { signal } = controller;

    async function loadHome() {
      setLoading(true);
      setError(null);

      // Nếu chưa set REACT_APP_API_URL thì không fetch (đỡ lỗi)
      if (!canFetch) {
        setProducts([]);
        setBestSelling([]);
        setDiscounted([]);
        setCurrentDrop(null);
        setLoading(false);
        return;
      }

      try {
        const [newList, bestList, discList] = await Promise.all([
          getNewProducts(signal),
          getBestSellingProducts(signal),
          getDiscountedProducts(signal),
        ]);

        setProducts((newList || []).map(mapToNewArrivalsItem));
        setBestSelling((bestList || []).map(mapToNewArrivalsItem));
        setDiscounted((discList || []).map(mapToNewArrivalsItem));

        // Drop: hiện chưa có endpoint drops => giữ null
        setCurrentDrop(null);
      } catch (err: any) {
        // Abort => không show error
        if (err?.name === "AbortError") return;
        console.error("Home API error:", err);
        setError(err?.message || t("home.errors.generic"));
      } finally {
        setLoading(false);
      }
    }

    loadHome();
    return () => controller.abort();
  }, [canFetch, t]);

  return (
    <main className="bg-brand-ivory text-brand-brown overflow-x-hidden">
      {/* ===== BACKGROUND WASH (premium, subtle) ===== */}
      <div className="pointer-events-none fixed inset-0 -z-10">
        <div className="absolute inset-0 bg-gradient-to-b from-brand-ivory via-brand-ivory to-brand-ivory" />
        <div className="absolute inset-0 bg-gradient-to-tr from-brand-gold/10 via-transparent to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-bl from-brand-brown/6 via-transparent to-transparent" />
      </div>

      {/* ===== HERO ===== */}
      <HeroSection
        title={t("home.hero.title")}
        subtitle={t("home.hero.subtitle")}
        description={t("home.hero.description")}
        imageUrl="/images/hero.jpg"
        ctaTo="/drops"
      />

      {/* ===== BODY WRAP ===== */}
      <div className="mx-auto max-w-7xl px-6 sm:px-8 lg:px-16">
        {/* ===== MICRO STRIP ===== */}
        <section className="py-10 md:py-12">
          <div className="rounded-[28px] border border-brand-brown/10 bg-brand-ivory/70 backdrop-blur-md px-6 md:px-10 py-8 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
            <div className="flex items-start gap-4">
              <div className="h-10 w-10 rounded-2xl border border-brand-brown/10 bg-brand-brown/5 grid place-items-center">
                <Sparkles className="w-5 h-5 text-brand-gold" />
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-[0.62em] text-brand-brown/55">
                  {t("home.micro.kicker")}
                </p>
                <p className="mt-2 text-sm text-brand-brown/60 leading-relaxed">
                  {t("home.micro.desc")}
                </p>
              </div>
            </div>

            <Link
              to="/collection"
              className="inline-flex items-center justify-center gap-2 h-11 px-6 rounded-full
                         border border-brand-brown/12 bg-transparent
                         text-[10px] uppercase tracking-[0.55em] text-brand-brown/70
                         hover:border-brand-brown/25 hover:text-brand-brown transition"
            >
              {t("home.micro.cta")} <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
        </section>

        {/* ===== ERROR STATE ===== */}
        {!loading && error && (
          <section className="pb-14">
            <div className="rounded-[24px] border border-red-500/20 bg-red-500/5 p-6">
              <p className="text-[10px] uppercase tracking-[0.55em] text-red-600/80">
                {t("home.errors.title")}
              </p>

              <p className="mt-3 text-sm text-red-600/80 leading-relaxed">
                {error}
              </p>

              {!API_URL && (
                <p className="mt-3 text-xs text-red-600/70">
                  {t("home.errors.missingApiUrl")}
                </p>
              )}
            </div>
          </section>
        )}

        {/* ===== NEW ARRIVALS ===== */}
        <section className="py-12 md:py-16">
          <SectionHead
            kicker={t("home.sections.new.kicker")}
            title={t("home.sections.new.title")}
            desc={t("home.sections.new.desc")}
            ctaText={t("home.common.viewAll")}
            ctaTo="/collection"
            icon={<PackageOpen className="w-4 h-4" />}
          />

          <div className="mt-10">
            {loading ? (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
                <SkeletonBlock className="h-[420px]" />
                <SkeletonBlock className="h-[420px]" />
                <SkeletonBlock className="h-[420px]" />
              </div>
            ) : products.length > 0 ? (
              <NewArrivals products={products} />
            ) : (
              <div className="rounded-[24px] border border-brand-brown/10 bg-brand-ivory/70 backdrop-blur-md p-8">
                <p className="text-[10px] uppercase tracking-[0.55em] text-brand-brown/55">
                  {t("home.empty.new.title")}
                </p>
                <p className="mt-3 text-sm text-brand-brown/60">
                  {t("home.empty.new.desc")}
                </p>
              </div>
            )}
          </div>
        </section>

        {/* ===== BEST SELLING ===== */}
        <section className="py-12 md:py-16">
          <SectionHead
            kicker={t("home.sections.best.kicker")}
            title={t("home.sections.best.title")}
            desc={t("home.sections.best.desc")}
            ctaText={t("home.common.viewAll")}
            ctaTo="/collection"
            icon={<Sparkles className="w-4 h-4" />}
          />

          <div className="mt-10">
            {loading ? (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
                <SkeletonBlock className="h-[420px]" />
                <SkeletonBlock className="h-[420px]" />
                <SkeletonBlock className="h-[420px]" />
              </div>
            ) : bestSelling.length > 0 ? (
              <NewArrivals products={bestSelling} />
            ) : (
              <div className="rounded-[24px] border border-brand-brown/10 bg-brand-ivory/70 backdrop-blur-md p-8">
                <p className="text-[10px] uppercase tracking-[0.55em] text-brand-brown/55">
                  {t("home.empty.best.title")}
                </p>
                <p className="mt-3 text-sm text-brand-brown/60">
                  {t("home.empty.best.desc")}
                </p>
              </div>
            )}
          </div>
        </section>

        {/* ===== DISCOUNTED ===== */}
        <section className="py-12 md:py-16">
          <SectionHead
            kicker={t("home.sections.sale.kicker")}
            title={t("home.sections.sale.title")}
            desc={t("home.sections.sale.desc")}
            ctaText={t("home.common.viewAll")}
            ctaTo="/collection"
            icon={<PackageOpen className="w-4 h-4" />}
          />

          <div className="mt-10">
            {loading ? (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
                <SkeletonBlock className="h-[420px]" />
                <SkeletonBlock className="h-[420px]" />
                <SkeletonBlock className="h-[420px]" />
              </div>
            ) : discounted.length > 0 ? (
              <NewArrivals products={discounted} />
            ) : (
              <div className="rounded-[24px] border border-brand-brown/10 bg-brand-ivory/70 backdrop-blur-md p-8">
                <p className="text-[10px] uppercase tracking-[0.55em] text-brand-brown/55">
                  {t("home.empty.sale.title")}
                </p>
                <p className="mt-3 text-sm text-brand-brown/60">
                  {t("home.empty.sale.desc")}
                </p>
              </div>
            )}
          </div>
        </section>

        {/* ===== CURRENT DROP ===== */}
        <section className="py-12 md:py-16">
          <SectionHead
            kicker={t("home.sections.drop.kicker")}
            title={t("home.sections.drop.title")}
            desc={t("home.sections.drop.desc")}
            ctaText={t("home.sections.drop.cta")}
            ctaTo="/drops"
            icon={<Sparkles className="w-4 h-4" />}
          />

          <div className="mt-10">
            {loading ? (
              <SkeletonBlock className="h-[340px]" />
            ) : currentDrop ? (
              <div className="rounded-[32px] border border-brand-brown/10 bg-brand-ivory/80 backdrop-blur-md shadow-[0_40px_120px_rgba(43,31,26,0.10)] overflow-hidden">
                <DropSection drop={currentDrop} />
              </div>
            ) : (
              <div className="rounded-[24px] border border-brand-brown/10 bg-brand-ivory/70 backdrop-blur-md p-8">
                <p className="text-[10px] uppercase tracking-[0.55em] text-brand-brown/55">
                  {t("home.empty.drop.title")}
                </p>
                <p className="mt-3 text-sm text-brand-brown/60">
                  {t("home.empty.drop.desc")}
                </p>
              </div>
            )}
          </div>
        </section>

        {/* ===== BRAND STORY ===== */}
        <section className="py-12 md:py-16">
          <SectionHead
            kicker={t("home.sections.story.kicker")}
            title={t("home.sections.story.title")}
            desc={t("home.sections.story.desc")}
            ctaText={t("home.sections.story.cta")}
            ctaTo="/story"
          />

          <div className="mt-10">
            <div className="rounded-[32px] border border-brand-brown/10 bg-brand-ivory/80 backdrop-blur-md shadow-[0_40px_120px_rgba(43,31,26,0.08)] overflow-hidden">
              <BrandStory />
            </div>
          </div>
        </section>

        <div className="pb-16" />
      </div>
    </main>
  );
}
